import React from "react";

export const cn = (...args) => args.filter(Boolean).join(" ");

export function Badge({ variant = "default", children, className = "", ...rest }) {
  const map = {
    profit: "bg-emerald-50 text-emerald-700 border-emerald-200",
    loss: "bg-red-50 text-red-700 border-red-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-indigo-50 text-indigo-700 border-indigo-200",
    muted: "bg-slate-100 text-slate-600 border-slate-200",
    default: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 border rounded-full text-[11px] font-medium uppercase tracking-wider",
        map[variant],
        className
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export function StatusPill({ status, label, testId }) {
  const isOk = ["connected", "active", "paper", "on"].includes(status);
  const isWarn = ["pending_code", "waiting", "warn", "loading"].includes(status);
  const dotClass = isOk ? "" : isWarn ? "warn" : "loss";
  const variant = isOk ? "profit" : isWarn ? "warn" : "loss";
  return (
    <Badge variant={variant} data-testid={testId}>
      <span className={cn("pulse-dot", dotClass)} />
      {label}
    </Badge>
  );
}

/**
 * Compact chip that shows the observed uptime % for a service over the last 24h.
 * Muted styling so it visually attaches to a preceding StatusPill without competing.
 * `data` is either `null` (no samples yet — chip hidden) or `{pct: 99.4, samples: 1420}`.
 * Color tone follows conventional SLA bands: ≥99% profit, 95-98.9% warn, <95% loss.
 */
export function UptimeChip({ data, testId }) {
  if (!data || data.pct == null) return null;
  const pct = data.pct;
  const variant = pct >= 99 ? "muted" : pct >= 95 ? "warn" : "loss";
  return (
    <Badge
      variant={variant}
      data-testid={testId}
      title={`Observed uptime over last 24h across ${data.samples} probes`}
      className="normal-case tracking-normal"
    >
      {pct.toFixed(1)}% 24h
    </Badge>
  );
}

export function MetricCard({ label, value, sub, tone = "neutral", testId, children }) {
  const toneClass = {
    profit: "text-emerald-600",
    loss: "text-red-600",
    warn: "text-amber-600",
    accent: "text-[#4F46E5]",
    neutral: "text-slate-900",
  }[tone];
  // Only a fixed-tone card (e.g. a warning that's always the same color, not
  // one that flips with a live PnL sign) gets a tinted background - tinting
  // every metric card would make a live-value card flash color as its sign
  // crosses zero, which reads as broken rather than informative.
  const bgClass = tone === "warn" ? "bg-[#FEF3E2] border-amber-200" : "";
  return (
    <div className={cn("card-surface p-5", bgClass)} data-testid={testId}>
      <p className="overline">{label}</p>
      <p className={cn("mono text-3xl font-semibold mt-2", toneClass)}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      {children}
    </div>
  );
}

/* Per-exchange breakdown row for MetricCard - "Blofin: $79.18  ·  WEEX: $136.29" */
export function MetricBreakdown({ items }) {
  const rows = (items || []).filter((i) => i.value != null);
  if (!rows.length) return null;
  return (
    <div className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap gap-x-3 gap-y-1">
      {rows.map((i) => (
        <span key={i.label} className="text-[11px] mono text-slate-600" data-testid={i.testId}>
          <span className="text-slate-400 uppercase">{i.label}</span>{" "}
          <span className={i.tone === "profit" ? "text-emerald-600" : i.tone === "loss" ? "text-red-600" : "text-slate-700"}>
            {i.value}
          </span>
        </span>
      ))}
    </div>
  );
}


export function Kv({ k, v }) {
  return (
    <div>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{k}</p>
      <p className="text-slate-900">{v}</p>
    </div>
  );
}
